import React, { useState } from 'react';
import { useDarkMode } from '../../../context/DarkModeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircleIcon, 
  ClockIcon, 
  TrashIcon,
  PencilIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import { QuarterlyPriority } from '../../../hooks/useQuarterlyPriorities';

interface PriorityItemProps {
  priority: QuarterlyPriority;
  onToggleComplete: (id: string) => void;
  onEdit?: (id: string, title: string, description?: string) => void;
  onDelete?: (id: string) => void;
}

const PriorityItem: React.FC<PriorityItemProps> = ({
  priority,
  onToggleComplete,
  onEdit,
  onDelete
}) => {
  const { darkMode } = useDarkMode();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(priority.priority_title);
  const [editDescription, setEditDescription] = useState(priority.priority_description || '');
  
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const itemBg = darkMode ? 'bg-gray-700/50' : 'bg-gray-50';
  const hoverBg = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100';
  

  const handleSaveEdit = () => {
    if (onEdit && editTitle.trim()) {
      onEdit(priority.id, editTitle.trim(), editDescription.trim() || undefined);
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditTitle(priority.priority_title);
    setEditDescription(priority.priority_description || '');
    setIsEditing(false);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className={`group rounded-lg ${itemBg} ${hoverBg} transition-all duration-200`}
    >
      <div className="p-3">
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <button
            onClick={() => onToggleComplete(priority.id)}
            className="flex-shrink-0 mt-0.5 transition-transform hover:scale-110"
            aria-label={priority.is_completed ? 'Mark as incomplete' : 'Mark as complete'}
          >
            {priority.is_completed ? (
              <CheckCircleIconSolid className="w-5 h-5 text-green-500" />
            ) : (
              <div className={`w-5 h-5 rounded-full border-2 ${
                darkMode ? 'border-gray-500 hover:border-purple-400' : 'border-gray-300 hover:border-purple-500'
              } transition-colors`} />
            )}
          </button>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className={`w-full px-2 py-1 rounded border ${
                    darkMode 
                      ? 'bg-gray-800 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit();
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                />
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className={`w-full px-2 py-1 rounded border ${
                    darkMode 
                      ? 'bg-gray-800 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                  rows={2}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveEdit}
                    className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div 
                  className="flex items-start gap-2 cursor-pointer"
                  onClick={() => priority.priority_description && setIsExpanded(!isExpanded)}
                >
                  <h4 className={`text-sm font-medium ${
                    priority.is_completed ? `line-through ${mutedTextColor}` : textColor
                  }`}>
                    {priority.priority_title}
                  </h4>
                  {priority.priority_description && (
                    <ChevronDownIcon 
                      className={`w-4 h-4 ${mutedTextColor} transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`} 
                    />
                  )}
                </div>

                <AnimatePresence>
                  {isExpanded && priority.priority_description && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className={`text-xs ${mutedTextColor} mt-1`}
                    >
                      {priority.priority_description}
                    </motion.p>
                  )}
                </AnimatePresence>

                {/* Metadata */}
                {priority.completion_date && (
                  <div className="flex items-center gap-3 mt-2">
                    <span className={`text-xs ${mutedTextColor}`}>
                      Completed {new Date(priority.completion_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Actions */}
          {!isEditing && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onEdit && !priority.is_completed && (
                <button
                  onClick={() => setIsEditing(true)}
                  className={`p-1 rounded ${
                    darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                  }`}
                  aria-label="Edit priority"
                >
                  <PencilIcon className="w-4 h-4 text-gray-500" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(priority.id)}
                  className={`p-1 rounded ${
                    darkMode ? 'hover:bg-red-900/50' : 'hover:bg-red-50'
                  }`}
                  aria-label="Delete priority"
                >
                  <TrashIcon className="w-4 h-4 text-red-500" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default PriorityItem;